import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Shield, ArrowLeft, CheckCircle2, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminRecovery = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/admin-recovery-request`, { email });
      setRequestSent(true);
      toast.success("If this is the admin email, a reset link has been sent.");
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/admin-reset-password`, {
        token,
        new_password: newPassword,
      });
      setResetComplete(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  // If we have a token, show the reset form
  if (token) {
    if (resetComplete) {
      return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6">
          <Card className="w-full max-w-md shadow-lg border-slate-200 text-center">
            <CardContent className="pt-8 pb-8">
              <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4 font-['Manrope']">
                Password Reset Complete
              </h2>
              <p className="text-slate-600 mb-6">
                Your admin password has been reset successfully. You can now login with your new password.
              </p>
              <Link to="/login">
                <Button className="bg-[#0F766E] hover:bg-[#115E59]">
                  Go to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6">
        <Card className="w-full max-w-md shadow-lg border-slate-200 overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-[#DC2626] to-[#0F766E]"></div>
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#DC2626]" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900 font-['Manrope']">
              Set New Password
            </CardTitle>
            <CardDescription className="text-slate-600">
              Enter your new admin password
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-11"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-[#DC2626] hover:bg-[#B91C1C]"
                disabled={loading}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Request reset form
  if (requestSent) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6">
        <Card className="w-full max-w-md shadow-lg border-slate-200 text-center">
          <CardContent className="pt-8 pb-8">
            <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 font-['Manrope']">
              Check Your Email
            </h2>
            <p className="text-slate-600 mb-6">
              If this email is registered as admin, a password reset link has been sent. Please check your inbox.
            </p>
            <p className="text-sm text-slate-500">
              The link will expire in 1 hour.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="p-6">
        <Link to="/login" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#0F766E] transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <Card className="w-full max-w-md shadow-lg border-slate-200 overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-[#DC2626] to-[#0F766E]"></div>
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#DC2626]" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900 font-['Manrope']">
              Admin Recovery
            </CardTitle>
            <CardDescription className="text-slate-600">
              Enter the admin email to receive a password reset link
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Admin Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter admin email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-[#DC2626] hover:bg-[#B91C1C]"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>
            <p className="text-xs text-slate-500 text-center mt-6">
              This page is for admin recovery only.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminRecovery;
